import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HorizontalBarChart } from '../../components/dashboard/HorizontalBarChart';
import { StatCard } from '../../components/dashboard/StatCard';
import { TrendBarChart } from '../../components/dashboard/TrendBarChart';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { usePermissions } from '../../contexts/PermissionsContext';
import { HomeDashboard, fetchHomeDashboard } from '../../services/dashboardApi';

const ENRICHMENT_LABELS: Record<string, string> = {
  Pending: 'Pendente',
  InProgress: 'Em progresso',
  Completed: 'Concluído',
  Failed: 'Falhou',
  Skipped: 'Ignorado',
};

const STAGE_LABELS: Record<string, string> = {
  Lead: 'Lead',
  Qualified: 'Qualificado',
  Proposal: 'Proposta',
  Negotiation: 'Negociação',
  Won: 'Ganho',
  Lost: 'Perdido',
};

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatNumber(value: number): string {
  return value.toLocaleString('pt-BR');
}

function mapChartLabels(
  items: HomeDashboard['operational']['prospectsByEnrichmentStatus'],
  labels: Record<string, string>,
) {
  return items.map((item) => ({
    ...item,
    label: labels[item.label] ?? item.label,
  }));
}

export function DashboardPage() {
  const { canAccessModule } = usePermissions();
  const [dashboard, setDashboard] = useState<HomeDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(false);
      try {
        setDashboard(await fetchHomeDashboard());
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <LoadingSpinner label="Carregando painel..." />;
  }

  if (error || !dashboard) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">
        Não foi possível carregar o painel. Tente novamente em instantes.
      </div>
    );
  }

  const { operational, executive, isExecutiveView } = dashboard;

  return (
    <PageShell variant="dashboard">
      <PageHeader
        title="Painel"
        description={`Atualizado em ${new Date(dashboard.generatedAtUtc).toLocaleString('pt-BR')}`}
        actions={
          canAccessModule('sales.prospects') ? (
            <Link to="/sales/prospects" className="erp-btn-primary">
              Ver prospects
            </Link>
          ) : undefined
        }
      />

      <section>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Leads na base"
            value={formatNumber(operational.prospectsTotal)}
            hint={`+${operational.prospectsThisMonth} neste mês`}
            tone="primary"
          />
          <StatCard
            label="Produtos ativos"
            value={formatNumber(operational.productsActive)}
            tone="emerald"
          />
          <StatCard
            label="Colaboradores"
            value={formatNumber(operational.collaboratorsActive)}
            tone="violet"
          />
          <StatCard
            label="Pipeline de oportunidades"
            value={formatCurrency(operational.opportunitiesPipelineValue)}
            hint={`${operational.opportunitiesTotal} oportunidade(s)`}
            tone="amber"
          />
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <StatCard label="Campanhas" value={formatNumber(operational.campaignsTotal)} tone="sky" />
          <StatCard
            label="Enriquecimento pendente"
            value={formatNumber(operational.prospectsPendingEnrichment)}
            hint="Aguardando ou em processamento"
            tone="orange"
          />
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        <TrendBarChart title="Leads capturados (últimos 7 dias)" points={operational.prospectsTrend} />
        <HorizontalBarChart
          title="Leads por campanha"
          items={operational.prospectsByCampaign}
          valueFormatter={(value) => formatNumber(value)}
        />
        <HorizontalBarChart
          title="Status de enriquecimento"
          items={mapChartLabels(operational.prospectsByEnrichmentStatus, ENRICHMENT_LABELS)}
          valueFormatter={(value) => formatNumber(value)}
        />
        <HorizontalBarChart
          title="Oportunidades por estágio"
          items={mapChartLabels(operational.opportunitiesByStage, STAGE_LABELS)}
          valueFormatter={(value) => formatCurrency(value)}
        />
      </section>

      {isExecutiveView && executive ? (
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">Visão executiva</h2>
            <p className="text-sm text-secondary-600">Métricas financeiras consolidadas das oportunidades ganhas.</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="MRR" value={formatCurrency(executive.mrr)} tone="primary" />
            <StatCard label="ARR" value={formatCurrency(executive.arr)} tone="indigo" />
            <StatCard label="Receita recorrente" value={formatCurrency(executive.recurringRevenue)} tone="emerald" />
            <StatCard
              label="Receita de implantação"
              value={formatCurrency(executive.implementationRevenue)}
              tone="teal"
            />
          </div>

          <StatCard label="Receita total ganha" value={formatCurrency(executive.totalWonRevenue)} tone="violet" />

          <div className="grid gap-4 xl:grid-cols-2">
            <HorizontalBarChart title="Receita por produto" items={executive.revenueByProduct} valueFormatter={formatCurrency} />
            <HorizontalBarChart title="Receita por projeto" items={executive.revenueByProject} valueFormatter={formatCurrency} />
            <HorizontalBarChart title="Receita por cliente" items={executive.revenueByClient} valueFormatter={formatCurrency} />
            <HorizontalBarChart title="Receita por segmento" items={executive.revenueBySegment} valueFormatter={formatCurrency} />
          </div>
        </section>
      ) : null}
    </PageShell>
  );
}
